/**
 * Tile Manager - Modular tile management system for 360° viewer
 * 
 * This module provides a clean, modular architecture for managing tiles in a 360° viewer
 * while preserving the original API for backward compatibility.
 */

import * as THREE from 'three';
import { TileManager } from './TileManager';
import type { DebugConfig } from './Debug';
import type { ReplacementPolicyConfig } from './ReplacementPolicy';

// Main facade (preserves original API)
export { TileManager } from './TileManager';

// Core services (for advanced usage)
export { TextureCache } from './TextureCache';
export { SpatialIndex } from './SpatialIndex';
export { SceneAdapter } from './SceneAdapter';
export { TileLoadQueue } from './TileLoadQueue';
export { LODController, TransitionState } from './LODController';
export { ViewportService } from './ViewportService';
export { ReplacementPolicy } from './ReplacementPolicy';
export { TransitionCoordinator } from './TransitionCoordinator';
export { Debug } from './Debug';

// Utilities
export { UrlBuilder } from './UrlBuilder';
export * from './utils';
export * from './constants';

// Type exports for configuration and events
export type { TextureCacheEvents, TextureCacheStats } from './TextureCache';
export type { SpatialIndexStats } from './SpatialIndex';
export type { SceneAdapterEvents } from './SceneAdapter';
export type { TileLoadQueueEvents, LoadQueueStats } from './TileLoadQueue';
export type { LODControllerEvents, LODControllerStats, LODChangeEvent } from './LODController';
export type { ViewportServiceEvents, ViewportServiceStats, ViewportChangeEvent, VisibleTilesResult } from './ViewportService';
export type { ReplacementDecision, CoverageAnalysis, ReplacementPolicyConfig } from './ReplacementPolicy';
export type { TransitionCoordinatorEvents, TransitionCoordinatorStats, TransitionCompleteCallback } from './TransitionCoordinator';
export type { DebugConfig, DebugMetrics, DebugTraceEvent } from './Debug';
export type { TileKeyComponents, CancellableOperation, DefaultMap } from './utils';

/**
 * Advanced configuration interface extending the base TileManagerConfig
 */
export interface AdvancedTileManagerConfig {
  // Core configuration
  metadata?: {
    url: string;
    levels: Array<{
      level: number;
      rows_per_face: number;
      cols_per_face: number;
    }>;
  };
  lodLevels?: Array<{
    level: number;
    tilesPerFace: number;
    fovThreshold: { min: number; max: number };
    priority: number;
  }>;
  maxCacheSize?: number;
  tilePadding?: number;
  transitionBuffer?: number;

  // Advanced options
  debug?: Partial<DebugConfig>;
  viewport?: {
    throttleMs?: number;
    stabilityMs?: number;
  };
  loading?: {
    maxConcurrent?: number;
    timeoutMs?: number;
  };
  replacement?: Partial<ReplacementPolicyConfig>;
  transitions?: {
    minTimeMs?: number;
    maxTimeMs?: number;
    rapidTransitionThreshold?: number;
  };
}

/**
 * Factory function for creating TileManager with advanced configuration
 */
export function createTileManager(
  scene: THREE.Scene,
  config?: AdvancedTileManagerConfig
): TileManager {
  // Convert advanced config to base config format
  const baseConfig = {
    metadata: config?.metadata,
    lodLevels: config?.lodLevels,
    maxCacheSize: config?.maxCacheSize,
    tilePadding: config?.tilePadding,
    transitionBuffer: config?.transitionBuffer,
  };

  const tileManager = new TileManager(scene, undefined, baseConfig);

  // Apply advanced configuration
  if (config?.debug) {
    tileManager.configureDebug(config.debug);
  }

  return tileManager;
}

/**
 * Utility function to validate tile manager configuration
 */
export function validateTileManagerConfig(config: AdvancedTileManagerConfig): {
  isValid: boolean;
  issues: string[];
  warnings: string[];
} {
  const issues: string[] = [];
  const warnings: string[] = [];

  // Validate metadata
  if (config.metadata) {
    if (!config.metadata.url) {
      issues.push('Metadata URL is required');
    }
    if (!config.metadata.levels || config.metadata.levels.length === 0) {
      issues.push('Metadata levels are required');
    }
  } else {
    warnings.push('No metadata configuration provided - tiles cannot be loaded');
  }

  // Validate LOD levels
  if (config.lodLevels) {
    const levels = config.lodLevels.map(lod => lod.level);
    const uniqueLevels = new Set(levels);
    if (uniqueLevels.size !== levels.length) {
      issues.push('Duplicate LOD levels found');
    }

    for (const lod of config.lodLevels) {
      if (lod.fovThreshold.min >= lod.fovThreshold.max) {
        issues.push(`Invalid FOV threshold for level ${lod.level}: min >= max`);
      }
      if (lod.tilesPerFace <= 0) {
        issues.push(`Invalid tilesPerFace for level ${lod.level}: must be > 0`);
      }
    }
  }

  // Validate cache size
  if (config.maxCacheSize !== undefined && config.maxCacheSize <= 0) {
    issues.push('maxCacheSize must be > 0');
  }

  // Validate loading configuration
  if (config.loading?.maxConcurrent !== undefined && config.loading.maxConcurrent <= 0) {
    issues.push('loading.maxConcurrent must be > 0');
  }

  return {
    isValid: issues.length === 0,
    issues,
    warnings,
  };
}

/**
 * Performance monitoring utilities
 */
export class TileManagerMonitor {
  private readonly tileManager: TileManager;
  private monitoringInterval?: number;
  private readonly callbacks = new Set<(stats: any) => void>();

  constructor(tileManager: TileManager) {
    this.tileManager = tileManager;
  }

  /**
   * Start monitoring performance metrics
   */
  startMonitoring(intervalMs = 5000): void {
    if (this.monitoringInterval) {
      this.stopMonitoring();
    }

    this.monitoringInterval = window.setInterval(() => {
      const stats = this.tileManager.getSystemStatus();
      const performance = this.tileManager.getPerformanceAnalysis();
      
      const monitorData = {
        timestamp: Date.now(),
        ...stats,
        performance,
      };

      this.callbacks.forEach(callback => {
        try {
          callback(monitorData);
        } catch (error) {
          // Silently handle callback errors
        }
      });
    }, intervalMs);
  }

  /**
   * Stop monitoring
   */
  stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = undefined;
    }
  }

  /**
   * Add monitoring callback
   */
  onUpdate(callback: (stats: any) => void): void {
    this.callbacks.add(callback);
  }

  /**
   * Remove monitoring callback
   */
  offUpdate(callback: (stats: any) => void): void {
    this.callbacks.delete(callback);
  }

  /**
   * Dispose monitor
   */
  dispose(): void {
    this.stopMonitoring();
    this.callbacks.clear();
  }
}
